import React, { useMemo, useState, useEffect } from "react";
import { View, StyleSheet } from "react-native";
import { TextInput } from "@/components/ui/TextInput";
import { PreviewSection, LookupField } from "../shared";
import { useEntityEdit } from "@/contexts/EntityEditContext";
import { TimecardType, TimeCardTimeType, TimeCardMetaType } from "@/types/db/time_cards";
import useDateTime from "@/hooks/useDateTime";
import { useTheme } from "@/hooks/useTheme";
import useAuth from "@/hooks/useAuth";
import { DateInput } from "@/components/ui/DateInput";
import { EnumInput } from "@/components/ui/EnumInput";
import { MarketsInput } from "@/components/inputs/MarketsInput";
import { getTimecardBillingOptions } from "@/config/tableConfigs/timecardTableConfig";
import { supabase } from "@/lib/supabase-client";

// Extended type for enriched data from timecardService.getTimecards()
interface TimecardDisplayData extends Omit<TimecardType, "meta_data"> {
  meta_data?: TimeCardMetaType;
  notes?: string | null;
  user?: {
    id?: string;
    name?: string;
  } | null;
  // Simplified linked_record shapes from service
  shift?: {
    id?: string;
    name?: string;
  } | null;
  project?: {
    id?: string;
    name?: string;
  } | null;
  client?: {
    id?: string;
    name?: string;
  } | null;
  // Enriched fields from service
  position?: string | null;
  billing_rate?: number | null;
  hours_worked?: number | null;
}

export interface TimecardOverviewTabProps {
  timecard: TimecardDisplayData | null;
  loading?: boolean;
}

export function TimecardOverviewTab({ timecard, loading = false }: TimecardOverviewTabProps) {
  const { editing, formData, updateFormData } = useEntityEdit<TimecardDisplayData>();
  const { formatTimestamp, fromUserTimezone } = useDateTime();
  const { colors } = useTheme();
  const { displayNames } = useAuth();

  // Get billing options with current theme colors
  const billingOptions = useMemo(() => getTimecardBillingOptions(colors), [colors]);

  // Unified data source
  const displayData = editing ? formData : timecard;
  const disabled = loading || !editing;

  // Computed values
  const workDate = displayData?.time?.start_time
    ? new Date(displayData.time.start_time).toISOString().split("T")[0]
    : "";

  const hoursWorked = displayData?.hours_worked ?? calculateLocalHours(displayData?.time);

  // Helper to update nested time object
  const updateTimeField = (field: keyof TimeCardTimeType, value: string | null) => {
    updateFormData({
      ...formData,
      time: {
        ...formData?.time,
        [field]: value,
      },
    });
  };

  // Helper to get user display name
  const getUserDisplayName = () => displayData?.user?.name;

  // Helper to get nested relation values
  const getShiftName = () => displayData?.shift?.name;
  const getProjectName = () => displayData?.project?.name;
  const getClientName = () => displayData?.client?.name;
  const getClientId = () => displayData?.client?.id;

  // ── Inherited markets from shift/project ──────────────────────
  const [inheritedMarkets, setInheritedMarkets] = useState<
    { id: string; name: string; fips_code?: string; label: string }[]
  >([]);
  const [locationMarket, setLocationMarket] = useState<{
    id: string;
    name: string;
    fips_code?: string;
    label: string;
  } | null>(null);

  useEffect(() => {
    const loadInheritedMarkets = async () => {
      const shiftId = displayData?.shift_id || timecard?.shift_id;
      const projectId = displayData?.project_id || timecard?.project_id;

      if (!shiftId && !projectId) {
        setInheritedMarkets([]);
        setLocationMarket(null);
        return;
      }

      try {
        // First try to get shift data (includes location and its own markets)
        if (shiftId) {
          const { data: shiftData } = await supabase
            .from("shifts")
            .select("location_id, meta_data, project:projects(location_id, meta_data)")
            .eq("id", shiftId)
            .single();

          if (shiftData) {
            // Determine which location to use (shift's own or project's)
            const locationId = shiftData.location_id || (shiftData.project as any)?.location_id;
            const locationSource = shiftData.location_id
              ? `From ${displayNames?.shifts?.name || "Shift"}`
              : `From ${displayNames?.projects?.name || "Project"}`;

            // Get location's market
            if (locationId) {
              const { data: location } = await supabase
                .schema("lists")
                .from("locations")
                .select("market_id")
                .eq("id", locationId)
                .single();

              if (location?.market_id) {
                const { data: market } = await supabase
                  .schema("lists")
                  .from("markets")
                  .select("id, name, fips_code")
                  .eq("id", location.market_id)
                  .maybeSingle();

                if (market) {
                  setLocationMarket({ ...market, label: locationSource });
                } else {
                  setLocationMarket(null);
                }
              } else {
                setLocationMarket(null);
              }
            } else {
              setLocationMarket(null);
            }

            // Collect additional markets from shift and project
            const allMarketIds: string[] = [];
            const marketSources: Record<string, string> = {};

            // Shift's own additional markets
            const shiftMarkets = (shiftData.meta_data as any)?.markets || [];
            shiftMarkets.forEach((id: string) => {
              if (!allMarketIds.includes(id)) {
                allMarketIds.push(id);
                marketSources[id] = `From ${displayNames?.shifts?.name || "Shift"}`;
              }
            });

            // Project's additional markets
            const projectMarkets = (shiftData.project as any)?.meta_data?.markets || [];
            projectMarkets.forEach((id: string) => {
              if (!allMarketIds.includes(id)) {
                allMarketIds.push(id);
                marketSources[id] = `From ${displayNames?.projects?.name || "Project"}`;
              }
            });

            if (allMarketIds.length > 0) {
              const { data: markets } = await supabase
                .schema("lists")
                .from("markets")
                .select("id, name, fips_code")
                .in("id", allMarketIds);

              setInheritedMarkets(
                (markets || []).map((m) => ({
                  ...m,
                  label: marketSources[m.id] || `From ${displayNames?.shifts?.name || "Shift"}`,
                })),
              );
            } else {
              setInheritedMarkets([]);
            }
            return;
          }
        }

        // Fallback: just project (no shift)
        if (projectId) {
          const { data: projectData } = await supabase
            .from("projects")
            .select("location_id, meta_data")
            .eq("id", projectId)
            .single();

          if (projectData) {
            // Get project location's market
            if (projectData.location_id) {
              const { data: location } = await supabase
                .schema("lists")
                .from("locations")
                .select("market_id")
                .eq("id", projectData.location_id)
                .single();

              if (location?.market_id) {
                const { data: market } = await supabase
                  .schema("lists")
                  .from("markets")
                  .select("id, name, fips_code")
                  .eq("id", location.market_id)
                  .maybeSingle();

                if (market) {
                  setLocationMarket({ ...market, label: `From ${displayNames?.projects?.name || "Project"}` });
                } else {
                  setLocationMarket(null);
                }
              } else {
                setLocationMarket(null);
              }
            } else {
              setLocationMarket(null);
            }

            // Project's additional markets
            const projectMarkets = (projectData.meta_data as any)?.markets || [];
            if (projectMarkets.length > 0) {
              const { data: markets } = await supabase
                .schema("lists")
                .from("markets")
                .select("id, name, fips_code")
                .in("id", projectMarkets);

              setInheritedMarkets(
                (markets || []).map((m) => ({
                  ...m,
                  label: `From ${displayNames?.projects?.name || "Project"}`,
                })),
              );
            } else {
              setInheritedMarkets([]);
            }
          }
        }
      } catch (err) {
        console.error("Failed to load inherited markets:", err);
        setInheritedMarkets([]);
        setLocationMarket(null);
      }
    };

    loadInheritedMarkets();
  }, [displayData?.shift_id, displayData?.project_id, timecard?.shift_id, timecard?.project_id, displayNames]);

  return (
    <View style={styles.content} pointerEvents={loading ? "none" : "auto"}>
      {/* Time & Hours Section */}
      <PreviewSection title="Time & Hours">
        <LookupField
          label="Date"
          value={workDate ? formatTimestamp(workDate, false) : undefined}
          placeholder="No date"
          readonly
          loading={loading}
        />
        <LookupField
          label="Hours Worked"
          value={hoursWorked !== null ? `${hoursWorked.toFixed(2)} hrs` : undefined}
          placeholder="—"
          readonly
          loading={loading}
        />
        <View style={{ flexDirection: "row", gap: 8 }}>
          <DateInput
            label="Start Time"
            mode="datetime"
            value={displayData?.time?.start_time}
            onChange={(date) => {
              const utcDate = fromUserTimezone(date);
              updateTimeField("start_time", utcDate.toISOString());
            }}
            editing={editing}
            disabled={disabled}
            loading={loading}
          />
          <DateInput
            label="End Time"
            mode="datetime"
            value={displayData?.time?.end_time}
            onChange={(date) => {
              const utcDate = fromUserTimezone(date);
              updateTimeField("end_time", utcDate.toISOString());
            }}
            editing={editing}
            disabled={disabled}
            loading={loading}
          />
        </View>
        <View style={{ flexDirection: "row", gap: 8 }}>
          <DateInput
            label="Meal Start"
            mode="datetime"
            value={displayData?.time?.meal_start}
            onChange={(date) => {
              const utcDate = fromUserTimezone(date);
              updateTimeField("meal_start", utcDate.toISOString());
            }}
            editing={editing}
            disabled={disabled}
            loading={loading}
            placeholder="No meal break"
          />
          <DateInput
            label="Meal End"
            mode="datetime"
            value={displayData?.time?.meal_end}
            onChange={(date) => {
              const utcDate = fromUserTimezone(date);
              updateTimeField("meal_end", utcDate.toISOString());
            }}
            editing={editing}
            disabled={disabled}
            loading={loading}
            placeholder="No meal break"
          />
        </View>
      </PreviewSection>

      {/* Billing Section */}
      <PreviewSection title="Billing">
        {editing ? (
          <EnumInput
            label="Billing"
            options={billingOptions}
            value={displayData?.billing}
            placeholder="Select billing type"
            disabled={!editing}
            onSelect={(value) => {
              updateFormData({ ...formData, billing: value });
            }}
          />
        ) : (
          <LookupField
            label="Billing"
            value={displayData?.billing || undefined}
            placeholder="Not set"
            readonly
            loading={loading}
          />
        )}
        <LookupField
          label="Position"
          value={displayData?.position || undefined}
          placeholder="No position"
          readonly
          loading={loading}
        />
        <LookupField
          label="Billing Rate"
          value={displayData?.billing_rate ? `$${displayData.billing_rate.toFixed(2)}/hr` : undefined}
          placeholder="Not set"
          readonly
          loading={loading}
        />
        <MarketsInput
          value={[]}
          inheritedMarket={locationMarket}
          inheritedMarkets={inheritedMarkets}
          state={loading ? "loading" : "idle"}
          onChange={() => {}}
        />
      </PreviewSection>

      {/* Notes Section */}
      <PreviewSection title="Notes">
        <TextInput
          label="Notes"
          value={displayData?.notes || ""}
          onChangeText={(text) => updateFormData({ ...formData, notes: text || null })}
          multiline
          numberOfLines={3}
          editable={editing}
          loading={loading}
        />
      </PreviewSection>

      {/* Related Section */}
      <PreviewSection title="Related">
        <LookupField
          label={displayNames?.users?.name || "User"}
          value={getUserDisplayName()}
          placeholder={`No ${displayNames?.users?.name?.toLowerCase() || "user"}`}
          icon="account"
          loading={loading}
          params={displayData?.user?.id ? { panel: "user", id: displayData.user.id } : undefined}
        />
        <LookupField
          label={displayNames?.shifts?.name || "Shift"}
          value={getShiftName()}
          placeholder={`No ${displayNames?.shifts?.name?.toLowerCase() || "shift"}`}
          icon="calendar-clock"
          loading={loading}
          params={displayData?.shift_id ? { panel: "shift", id: displayData.shift_id } : undefined}
        />
        <LookupField
          label={displayNames?.projects?.name || "Project"}
          value={getProjectName()}
          placeholder={`No ${displayNames?.projects?.name?.toLowerCase() || "project"}`}
          icon="briefcase"
          loading={loading}
          params={displayData?.project_id ? { panel: "project", id: displayData.project_id } : undefined}
        />
        <LookupField
          label={displayNames?.clients?.name || "Client"}
          value={getClientName()}
          placeholder={`No ${displayNames?.clients?.name?.toLowerCase() || "client"}`}
          icon="office-building"
          loading={loading}
          params={getClientId() ? { panel: "client", id: getClientId()! } : undefined}
        />
      </PreviewSection>
    </View>
  );
}

// Calculate hours locally if not provided by service
function calculateLocalHours(time?: TimeCardTimeType): number | null {
  if (!time?.start_time || !time?.end_time) return null;

  const startTime = new Date(time.start_time);
  const endTime = new Date(time.end_time);
  let totalMs = endTime.getTime() - startTime.getTime();

  // Subtract meal break if present
  if (time.meal_start && time.meal_end) {
    const mealStart = new Date(time.meal_start);
    const mealEnd = new Date(time.meal_end);
    const mealMs = mealEnd.getTime() - mealStart.getTime();
    totalMs -= mealMs;
  }

  return totalMs / (1000 * 60 * 60); // Convert to hours
}

const styles = StyleSheet.create({
  content: {
    flex: 1,
    padding: 16,
    gap: 16,
    overflowY: "scroll",
  },
});
